import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";

function addProcessingTrackListeners(videoTrack, trackGenerator, stopProcessing, onEnded) {
  videoTrack.addEventListener(
    "ended",
    () => {
      if (stopProcessing()) onEnded?.();
    },
    { once: true }
  );
  trackGenerator.addEventListener("ended", stopProcessing, { once: true });
}

function processWithTrackProcessor(videoTrack, canvas, selfieSegmentation) {
  const trackProcessor = new MediaStreamTrackProcessor({ track: videoTrack });
  const trackGenerator = new MediaStreamTrackGenerator({ kind: "video" });
  let stopped = false;

  const stopProcessing = () => {
    if (stopped) return false;
    stopped = true;
    videoTrack.stop();
    trackGenerator.stop();
    selfieSegmentation.close();
    return true;
  };

  const transformer = new TransformStream({
    async transform(videoFrame, controller) {
      try {
        // The canvas backs the outgoing frames, so it has to follow the camera resolution instead of
        // staying at the square placeholder size, which downscaled what other players receive.
        if (canvas.width !== videoFrame.displayWidth || canvas.height !== videoFrame.displayHeight) {
          canvas.width = videoFrame.displayWidth;
          canvas.height = videoFrame.displayHeight;
        }
        // Mediapipe reads width/height off the image it is given, which a VideoFrame does not expose.
        videoFrame.width = videoFrame.displayWidth;
        videoFrame.height = videoFrame.displayHeight;
        await selfieSegmentation.send({ image: videoFrame });
        const timestamp = videoFrame.timestamp;
        const newFrame = new VideoFrame(canvas, { timestamp });
        controller.enqueue(newFrame);
      } finally {
        videoFrame.close();
      }
    }
  });

  trackProcessor.readable
    .pipeThrough(transformer)
    .pipeTo(trackGenerator.writable)
    .catch(error => {
      if (!stopped) console.error("Failed to process video frame.", error);
      stopProcessing();
    });

  return { trackGenerator, stopProcessing };
}

async function processWithCanvas(videoTrack, canvas, selfieSegmentation) {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([videoTrack]);

  try {
    await video.play();
  } catch (error) {
    video.pause();
    video.srcObject = null;
    videoTrack.stop();
    await selfieSegmentation.close();
    throw error;
  }

  canvas.width = video.videoWidth || 300;
  canvas.height = video.videoHeight || 300;
  const trackGenerator = canvas.captureStream(30).getVideoTracks()[0];
  let frameCallbackId;
  let stopped = false;

  const stopProcessing = () => {
    if (stopped) return false;
    stopped = true;

    if (frameCallbackId !== undefined) {
      if (typeof video.cancelVideoFrameCallback === "function") {
        video.cancelVideoFrameCallback(frameCallbackId);
      } else {
        cancelAnimationFrame(frameCallbackId);
      }
    }

    video.pause();
    video.srcObject = null;
    videoTrack.stop();
    trackGenerator.stop();
    selfieSegmentation.close();
    return true;
  };

  const requestFrame = callback =>
    typeof video.requestVideoFrameCallback === "function"
      ? video.requestVideoFrameCallback(callback)
      : requestAnimationFrame(callback);

  const processFrame = async () => {
    if (stopped) return;

    try {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        canvas.width = video.videoWidth || canvas.width;
        canvas.height = video.videoHeight || canvas.height;
        await selfieSegmentation.send({ image: video });
      }
      frameCallbackId = requestFrame(processFrame);
    } catch (error) {
      console.error("Failed to process video frame.", error);
      stopProcessing();
    }
  };

  frameCallbackId = requestFrame(processFrame);
  return { trackGenerator, stopProcessing };
}

export async function process_stream(videoTracks, backgroundColor = null, onEnded) {
  const processedStream = new MediaStream();
  const useTrackProcessor =
    typeof MediaStreamTrackProcessor !== "undefined" && typeof MediaStreamTrackGenerator !== "undefined";

  for (const videoTrack of videoTracks) {
    const canvas = useTrackProcessor ? new OffscreenCanvas(300, 300) : document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Unable to create a canvas context for video processing.");

    const selfieSegmentation = new SelfieSegmentation({
      locateFile: file => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
    });

    selfieSegmentation.setOptions({
      modelSelection: 0
    });

    selfieSegmentation.onResults(async results => {
      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(results.segmentationMask, 0, 0, canvas.width, canvas.height);

      if (backgroundColor) {
        ctx.globalCompositeOperation = "source-out";
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "destination-atop";
      }

      ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
      ctx.restore();
    });

    const { trackGenerator, stopProcessing } = useTrackProcessor
      ? processWithTrackProcessor(videoTrack, canvas, selfieSegmentation)
      : await processWithCanvas(videoTrack, canvas, selfieSegmentation);

    addProcessingTrackListeners(videoTrack, trackGenerator, stopProcessing, onEnded);
    trackGenerator._hubs_stopProcessing = stopProcessing;
    processedStream.addTrack(trackGenerator);
  }

  return processedStream;
}
